import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { useNotifications } from '@sequencehq/core-components'
import { useMutation } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'

import { useCurrentUser } from 'common/hooks'
import { automationsPaths } from 'Automations/router'
import { CustomerInfoReviewOutcome } from '../types'

/**
 * Dismisses a customer-info review task (the `DISMISSED` outcome): nothing is
 * applied and the task leaves the inbox — the same completion the review
 * modal's "Dismiss" action performs. Exposes `isPending` from the mutation so
 * the trigger can show its own loading state without owning any local state.
 */
export const useDismissCustomerInfoReviewTask = ({
  taskId,
  customerName
}: {
  taskId: string
  customerName: string
}) => {
  const { displayNotification } = useNotifications()
  const navigate = useNavigate()
  const currentUser = useCurrentUser()

  const dismissMutation = useMutation(
    dashboardv99990101Client.postCompleteManualTask
  )

  const dismiss = useCallback(async () => {
    const dismissOutcome: CustomerInfoReviewOutcome = { outcome: 'DISMISSED' }
    try {
      await dismissMutation.mutateAsync({
        id: taskId,
        outputData: { ...dismissOutcome, completedBy: currentUser.id }
      })
      displayNotification(`Dismissed proposed changes for ${customerName}`, {
        type: 'success'
      })
      navigate(automationsPaths.manualTasksInbox())
    } catch {
      displayNotification('Failed to dismiss task', { type: 'error' })
    }
  }, [
    dismissMutation,
    taskId,
    currentUser.id,
    customerName,
    displayNotification,
    navigate
  ])

  return { dismiss, isPending: dismissMutation.isPending }
}
